import React, { useState, useEffect, useCallback } from 'react';
import b2bService from '../../services/b2bService';

const B2bTopupApprovalPanel = () => {
  const [requests, setRequests] = useState([]);
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState('');
  const [msg, setMsg] = useState(null);

  // Approval modal state
  const [approvingReq, setApprovingReq] = useState(null);
  const [approveRemarks, setApproveRemarks] = useState('');
  const [submittingApprove, setSubmittingApprove] = useState(false);

  // Rejection modal state
  const [rejectingReq, setRejectingReq] = useState(null);
  const [rejectReason, setRejectReason] = useState('');
  const [submittingReject, setSubmittingReject] = useState(false);

  const fetchRequests = useCallback(async () => {
    setLoading(true);
    try {
      const data = await b2bService.getPendingTopups();
      setRequests(Array.isArray(data) ? data : []);
    } catch (err) {
      setMsg({ type: 'error', text: err.response?.data?.message || err.message || 'Failed to load topup requests' });
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchRequests();
  }, [fetchRequests]);

  const handleApprove = async (e) => {
    e.preventDefault();
    if (!approvingReq) return;
    setSubmittingApprove(true);
    try {
      const res = await b2bService.approveTopup(approvingReq.requestId, approveRemarks);
      const receiptNo = res?.advanceReceiptNumber || res?.AdvanceReceiptNumber || 'ADVANCE';
      setMsg({
        type: 'success',
        text: `✓ Request #${approvingReq.requestNumber} approved! Advance receipt #${receiptNo} created and ₹${Number(approvingReq.requestedAmount).toLocaleString('en-IN')} credited to ${approvingReq.partyName}'s wallet.`
      });
      setApprovingReq(null);
      setApproveRemarks('');
      await fetchRequests();
    } catch (err) {
      setMsg({ type: 'error', text: err.response?.data?.message || err.message || 'Approval failed' });
    } finally {
      setSubmittingApprove(false);
    }
  };

  const handleReject = async (e) => {
    e.preventDefault();
    if (!rejectingReq) return;
    if (!rejectReason.trim()) {
      alert('Please provide a reason for rejection.');
      return;
    }
    setSubmittingReject(true);
    try {
      await b2bService.rejectTopup(rejectingReq.requestId, rejectReason.trim());
      setMsg({
        type: 'success',
        text: `Request #${rejectingReq.requestNumber} has been rejected. No wallet balance was credited.`
      });
      setRejectingReq(null);
      setRejectReason('');
      await fetchRequests();
    } catch (err) {
      setMsg({ type: 'error', text: err.response?.data?.message || err.message || 'Rejection failed' });
    } finally {
      setSubmittingReject(false);
    }
  };

  const filtered = requests.filter((r) => {
    if (!search.trim()) return true;
    const q = search.toLowerCase();
    return (
      (r.requestNumber && r.requestNumber.toLowerCase().includes(q)) ||
      (r.partyName && r.partyName.toLowerCase().includes(q)) ||
      (r.partyCode && r.partyCode.toLowerCase().includes(q)) ||
      (r.transactionReference && r.transactionReference.toLowerCase().includes(q))
    );
  });

  const totalValuePending = requests.reduce((sum, r) => sum + Number(r.requestedAmount || 0), 0);

  return (
    <div className="b2b-topup-panel" style={{ padding: '4px 0' }}>
      {/* ── Summary Strip ── */}
      <div style={{ display: 'flex', gap: '16px', marginBottom: '16px', flexWrap: 'wrap', alignItems: 'center' }}>
        <div style={{ background: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: '8px', padding: '12px 18px', flex: '1', minWidth: '220px' }}>
          <div style={{ fontSize: '12px', color: '#64748b', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.04em' }}>
            Pending Verification
          </div>
          <div style={{ fontSize: '24px', fontWeight: 700, color: '#0f172a', marginTop: '4px' }}>
            {requests.length} Requests
          </div>
        </div>

        <div style={{ background: '#f0fdf4', border: '1px solid #bbf7d0', borderRadius: '8px', padding: '12px 18px', flex: '1', minWidth: '220px' }}>
          <div style={{ fontSize: '12px', color: '#166534', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.04em' }}>
            Total Value Pending Credit
          </div>
          <div style={{ fontSize: '24px', fontWeight: 700, color: '#15803d', marginTop: '4px' }}>
            ₹{totalValuePending.toLocaleString('en-IN', { minimumFractionDigits: 2 })}
          </div>
        </div>

        <div style={{ display: 'flex', gap: '10px', alignItems: 'center', marginLeft: 'auto' }}>
          <input
            type="text"
            className="b2b-form-input"
            style={{ width: '280px', height: '38px' }}
            placeholder="Search Party, UTR, Request #..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
          <button
            type="button"
            className="b2b-refresh-btn"
            style={{ padding: '8px 14px', height: '38px' }}
            onClick={fetchRequests}
            disabled={loading}
          >
            {loading ? 'Refreshing...' : '🔄 Refresh'}
          </button>
        </div>
      </div>

      {msg && (
        <div
          style={{
            padding: '10px 14px',
            marginBottom: '16px',
            borderRadius: '6px',
            fontSize: '13.5px',
            background: msg.type === 'error' ? '#fef2f2' : '#f0fdf4',
            color: msg.type === 'error' ? '#b91c1c' : '#15803d',
            border: `1px solid ${msg.type === 'error' ? '#fecaca' : '#bbf7d0'}`
          }}
        >
          {msg.text}
        </div>
      )}

      {/* ── Table ── */}
      {loading && requests.length === 0 ? (
        <div style={{ textAlign: 'center', padding: '40px', color: '#64748b' }}>
          Loading pending top-up verification requests...
        </div>
      ) : filtered.length === 0 ? (
        <div style={{ textAlign: 'center', padding: '50px 20px', background: '#f8fafc', borderRadius: '8px', border: '1px dashed #cbd5e1' }}>
          <div style={{ fontSize: '28px', marginBottom: '8px' }}>✓</div>
          <div style={{ fontSize: '16px', fontWeight: 600, color: '#334155' }}>All Top-Up Requests Cleared</div>
          <div style={{ fontSize: '13px', color: '#64748b', marginTop: '4px' }}>
            No pending wallet deposit requests from client facilities.
          </div>
        </div>
      ) : (
        <div className="b2b-table-container" style={{ overflowX: 'auto' }}>
          <table className="b2b-table" style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead>
              <tr style={{ background: '#f8fafc', borderBottom: '2px solid #e2e8f0', textAlign: 'left', fontSize: '12px', color: '#475569' }}>
                <th style={{ padding: '10px 12px' }}>Request #</th>
                <th style={{ padding: '10px 12px' }}>Date</th>
                <th style={{ padding: '10px 12px' }}>Party / Facility</th>
                <th style={{ padding: '10px 12px' }}>Amount (₹)</th>
                <th style={{ padding: '10px 12px' }}>Channel</th>
                <th style={{ padding: '10px 12px' }}>Bank Ref / UTR</th>
                <th style={{ padding: '10px 12px' }}>Requested By</th>
                <th style={{ padding: '10px 12px', textAlign: 'right' }}>Actions</th>
              </tr>
            </thead>
            <tbody>
              {filtered.map((req) => (
                <tr key={req.requestId} style={{ borderBottom: '1px solid #f1f5f9', fontSize: '13.5px' }}>
                  <td style={{ padding: '12px', fontWeight: 600, color: '#2563eb' }}>
                    {req.requestNumber}
                  </td>
                  <td style={{ padding: '12px', color: '#64748b' }}>
                    {new Date(req.requestedDate).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' })}
                    <div style={{ fontSize: '11px', color: '#94a3b8' }}>
                      {new Date(req.requestedDate).toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit' })}
                    </div>
                  </td>
                  <td style={{ padding: '12px' }}>
                    <div style={{ fontWeight: 600, color: '#0f172a' }}>{req.partyName}</div>
                    <div style={{ fontSize: '11.5px', color: '#64748b' }}>Code: {req.partyCode}</div>
                  </td>
                  <td style={{ padding: '12px', fontWeight: 700, color: '#16a34a', fontSize: '14.5px' }}>
                    ₹{Number(req.requestedAmount).toLocaleString('en-IN', { minimumFractionDigits: 2 })}
                  </td>
                  <td style={{ padding: '12px' }}>
                    <span style={{ background: '#e0f2fe', color: '#0369a1', padding: '3px 8px', borderRadius: '4px', fontSize: '11.5px', fontWeight: 600 }}>
                      {req.paymentMode}
                    </span>
                  </td>
                  <td style={{ padding: '12px' }}>
                    <div style={{ fontFamily: 'monospace', fontWeight: 600, color: '#334155' }}>
                      {req.transactionReference}
                    </div>
                    {req.bankName && <div style={{ fontSize: '11px', color: '#64748b' }}>{req.bankName}</div>}
                    {req.requestNotes && <div style={{ fontSize: '11.5px', color: '#94a3b8', fontStyle: 'italic' }}>{req.requestNotes}</div>}
                  </td>
                  <td style={{ padding: '12px', color: '#475569' }}>
                    {req.requestedByUsername}
                  </td>
                  <td style={{ padding: '12px', textAlign: 'right' }}>
                    <div style={{ display: 'inline-flex', gap: '8px' }}>
                      <button
                        type="button"
                        style={{
                          background: '#16a34a',
                          color: '#fff',
                          border: 'none',
                          borderRadius: '5px',
                          padding: '6px 12px',
                          fontSize: '12.5px',
                          fontWeight: 600,
                          cursor: 'pointer'
                        }}
                        onClick={() => {
                          setApprovingReq(req);
                          setApproveRemarks(`Bank statement credit verified on ${new Date().toLocaleDateString('en-IN')}`);
                        }}
                      >
                        ✓ Approve & Credit
                      </button>
                      <button
                        type="button"
                        style={{
                          background: '#fff',
                          color: '#dc2626',
                          border: '1px solid #fca5a5',
                          borderRadius: '5px',
                          padding: '6px 12px',
                          fontSize: '12.5px',
                          fontWeight: 600,
                          cursor: 'pointer'
                        }}
                        onClick={() => {
                          setRejectingReq(req);
                          setRejectReason('Transaction Reference / UTR not found in bank statement.');
                        }}
                      >
                        ✕ Reject
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* ── Approve Modal ── */}
      {approvingReq && (
        <div className="b2b-modal-backdrop" onClick={() => !submittingApprove && setApprovingReq(null)}>
          <div className="b2b-modal-window" style={{ maxWidth: '480px' }} onClick={(e) => e.stopPropagation()}>
            <div className="b2b-modal-header">
              <h3 className="b2b-modal-title">Confirm Bank Deposit Approval</h3>
              <button type="button" className="b2b-modal-close-btn" onClick={() => setApprovingReq(null)}>✕</button>
            </div>
            <form onSubmit={handleApprove}>
              <div className="b2b-modal-body">
                <div style={{ background: '#f0fdf4', border: '1px solid #bbf7d0', borderRadius: '6px', padding: '12px', marginBottom: '14px' }}>
                  <div style={{ fontSize: '13px', color: '#166534' }}>
                    You are approving <strong>₹{Number(approvingReq.requestedAmount).toLocaleString('en-IN', { minimumFractionDigits: 2 })}</strong> for:
                  </div>
                  <div style={{ fontSize: '15px', fontWeight: 700, color: '#15803d', marginTop: '2px' }}>
                    {approvingReq.partyName} ({approvingReq.partyCode})
                  </div>
                  <div style={{ fontSize: '12.5px', color: '#166534', marginTop: '6px' }}>
                    UTR / Ref: <strong style={{ fontFamily: 'monospace' }}>{approvingReq.transactionReference}</strong> ({approvingReq.paymentMode})
                  </div>
                </div>

                <div className="b2b-form-group">
                  <label className="b2b-form-label">Audit Remarks / Verification Notes</label>
                  <input
                    type="text"
                    className="b2b-form-input"
                    value={approveRemarks}
                    onChange={(e) => setApproveRemarks(e.target.value)}
                    placeholder="e.g. Verified with HDFC statement credit..."
                  />
                  <span className="b2b-form-hint">
                    Approval will instantly generate an audited Advance Receipt and credit the facility's wallet balance.
                  </span>
                </div>
              </div>
              <div className="b2b-modal-footer">
                <button
                  type="button"
                  className="b2b-btn-secondary"
                  onClick={() => setApprovingReq(null)}
                  disabled={submittingApprove}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  style={{
                    background: '#16a34a',
                    color: '#fff',
                    border: 'none',
                    padding: '8px 16px',
                    borderRadius: '6px',
                    fontWeight: 600,
                    cursor: 'pointer'
                  }}
                  disabled={submittingApprove}
                >
                  {submittingApprove ? 'Crediting Wallet...' : 'Confirm & Credit Wallet'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* ── Reject Modal ── */}
      {rejectingReq && (
        <div className="b2b-modal-backdrop" onClick={() => !submittingReject && setRejectingReq(null)}>
          <div className="b2b-modal-window" style={{ maxWidth: '480px' }} onClick={(e) => e.stopPropagation()}>
            <div className="b2b-modal-header">
              <h3 className="b2b-modal-title">Reject Top-Up Deposit Request</h3>
              <button type="button" className="b2b-modal-close-btn" onClick={() => setRejectingReq(null)}>✕</button>
            </div>
            <form onSubmit={handleReject}>
              <div className="b2b-modal-body">
                <div style={{ background: '#fef2f2', border: '1px solid #fecaca', borderRadius: '6px', padding: '12px', marginBottom: '14px' }}>
                  <div style={{ fontSize: '13px', color: '#991b1b' }}>
                    Rejecting request for <strong>₹{Number(rejectingReq.requestedAmount).toLocaleString('en-IN', { minimumFractionDigits: 2 })}</strong> from <strong>{rejectingReq.partyName}</strong>.
                  </div>
                  <div style={{ fontSize: '12.5px', color: '#991b1b', marginTop: '4px' }}>
                    UTR: <strong style={{ fontFamily: 'monospace' }}>{rejectingReq.transactionReference}</strong>
                  </div>
                </div>

                <div className="b2b-form-group">
                  <label className="b2b-form-label">Rejection Reason <span style={{ color: '#ef4444' }}>*</span></label>
                  <textarea
                    rows={3}
                    className="b2b-form-textarea"
                    value={rejectReason}
                    onChange={(e) => setRejectReason(e.target.value)}
                    placeholder="Explain why the deposit is rejected (will be visible to the client)..."
                    required
                  />
                </div>
              </div>
              <div className="b2b-modal-footer">
                <button
                  type="button"
                  className="b2b-btn-secondary"
                  onClick={() => setRejectingReq(null)}
                  disabled={submittingReject}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  style={{
                    background: '#dc2626',
                    color: '#fff',
                    border: 'none',
                    padding: '8px 16px',
                    borderRadius: '6px',
                    fontWeight: 600,
                    cursor: 'pointer'
                  }}
                  disabled={submittingReject}
                >
                  {submittingReject ? 'Rejecting...' : 'Confirm Rejection'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default B2bTopupApprovalPanel;

